import React, { useState } from 'react';
import { X, Play, ArrowRight, Lightning, PencilSimple, Microphone } from 'phosphor-react';
// changed by Cursor: import useTheme for dark mode inline styles
import { useTheme } from '../../../contexts/ThemeContext';

const OverallPanel = () => {
  const [fullscreenVideo, setFullscreenVideo] = useState(null);
  // changed by Cursor: dark mode support for inline styles
  const { isDarkMode } = useTheme();

  const videos = [
    { id: 1, embedId: '0cIMizic9l8', title: 'The Importance of Deliberate Practice' },
    { id: 2, embedId: '7iuM3V16T7M', title: 'How to Learn with ZenVokus' }
  ];

  const openFullscreen = (video) => {
    setFullscreenVideo(video);
    document.body.style.overflow = 'hidden';
  };

  const closeFullscreen = () => {
    setFullscreenVideo(null);
    document.body.style.overflow = '';
  };

  // Section Title Component - using theme color #1d4746
  // changed by Cursor: dark mode support
  const SectionTitle = ({ children }) => (
    <div className="text-white px-4 py-2.5 rounded-t-lg" style={{ background: 'linear-gradient(135deg, #1d4746 0%, #236463 100%)' }}>
      <h2 className="text-lg font-medium">{children}</h2>
    </div>
  );

  // Practice Card Component - visual like the image
  // changed by Cursor: dark mode support with dark: classes
  const PracticeCard = ({ icon: Icon, title, description }) => (
    <div className="bg-white dark:bg-slate-800 p-5 rounded-2xl border border-stone-200 dark:border-slate-700 shadow-sm">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={20} weight="fill" style={{ color: '#1d4746' }} />
        <span className="font-semibold text-stone-800 dark:text-slate-200">{title}</span>
      </div>
      <div className="flex items-center gap-4 mb-3">
        <div className="flex flex-col">
          <span className="text-xs text-stone-400 dark:text-slate-500 uppercase tracking-wide">Level</span>
          <span className="text-3xl font-bold text-stone-800 dark:text-slate-200">0</span>
        </div>
        <div className="h-10 w-px bg-stone-200 dark:bg-slate-700"></div>
        <div className="flex-1">
          <span className="text-xs text-stone-400 dark:text-slate-500 uppercase tracking-wide">Progress</span>
          <div className="w-full h-2 bg-stone-100 dark:bg-slate-700 rounded-full mt-1.5 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: '30%', backgroundColor: '#1d4746' }}></div>
          </div>
          <span className="text-xs text-stone-400 dark:text-slate-500 mt-1 block">0% To next level</span>
        </div>
      </div>
      <p className="text-sm text-stone-500 dark:text-slate-400">{description}</p>
    </div>
  );

  return (
    // changed by Cursor: Increased bottom padding for mobile (pb-[100px] on mobile, pb-32 on md+)
    <div className="w-full min-h-screen bg-stone-50 dark:bg-slate-900 px-6 md:px-10 py-8 pb-[100px] md:pb-32">
      <div className="w-full">
        
        {/* Row 1: Intro (left) + Videos (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Apa itu ZenVokus - LEFT */}
          <div className="rounded-lg overflow-hidden border border-stone-200 dark:border-slate-700">
            <SectionTitle>Apa itu ZenVokus?</SectionTitle>
            <div className="bg-white dark:bg-slate-800 p-6">
              <p className="text-base text-stone-600 dark:text-slate-400 leading-relaxed mb-4">
                ZenVokus adalah platform belajar bahasa berbasis <strong className="font-semibold">kemampuan nyata</strong>, bukan sekadar menyelesaikan materi.
              </p>
              <p className="text-base text-stone-600 dark:text-slate-400 mb-3">Kamu akan:</p>
              <ul className="text-base text-stone-500 dark:text-slate-400 space-y-2">
                <li>· Belajar lewat lesson terstruktur</li>
                <li>· Berlatih lewat AI practice</li>
                <li>· Naik level berdasarkan performa, bukan checklist</li>
              </ul>
            </div>
          </div>

          {/* Videos - RIGHT (Theme Teal) */}
          {/* changed by Cursor: dark mode support for inline styles */}
          <div className="rounded-lg overflow-hidden" style={{ border: isDarkMode ? '1px solid #334155' : '1px solid #236463' }}>
            <div className="text-white px-4 py-2.5" style={{ background: 'linear-gradient(135deg, #1d4746 0%, #236463 100%)' }}>
              <h2 className="text-lg font-medium">Tutorial Videos</h2>
            </div>
            <div className="p-6" style={{ backgroundColor: isDarkMode ? '#1e293b' : '#e8f5f4' }}>
              <div className="grid grid-cols-2 gap-5">
                {videos.map((video) => (
                  <div key={video.id} className="group cursor-pointer" onClick={() => openFullscreen(video)}>
                    <div className="relative aspect-video bg-stone-200 dark:bg-slate-700 rounded-lg overflow-hidden mb-3 shadow-sm">
                      <img src={`https://img.youtube.com/vi/${video.embedId}/maxresdefault.jpg`} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                          <Play size={18} weight="fill" className="ml-0.5" style={{ color: '#1d4746' }} />
                        </div>
                      </div>
                    </div>
                    <p className="text-sm leading-snug font-medium" style={{ color: isDarkMode ? '#94a3b8' : '#1d4746' }}>{video.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Struktur Belajar */}
        <div className="rounded-lg overflow-hidden border border-stone-200 dark:border-slate-700 mb-8">
          <SectionTitle>Struktur Belajar</SectionTitle>
          <div className="bg-white dark:bg-slate-800 p-6">
            <p className="text-base text-stone-600 dark:text-slate-400 mb-5">Ini penting untuk dipahami.</p>
            
            {/* changed by Cursor: dark mode for inline badge styles */}
            <div className="flex items-center gap-3 text-sm text-stone-500 dark:text-slate-400 mb-5 flex-wrap">
              <span className="px-3 py-1.5 rounded" style={{ backgroundColor: isDarkMode ? '#1e3a3a' : '#e8f5f4', color: isDarkMode ? '#5eead4' : '#1d4746' }}>Course</span>
              <ArrowRight size={14} className="text-stone-400 dark:text-slate-500" />
              <span className="px-3 py-1.5 rounded" style={{ backgroundColor: isDarkMode ? '#1e3a3a' : '#e8f5f4', color: isDarkMode ? '#5eead4' : '#1d4746' }}>Subcourse</span>
              <ArrowRight size={14} className="text-stone-400 dark:text-slate-500" />
              <span className="px-3 py-1.5 rounded" style={{ backgroundColor: isDarkMode ? '#1e3a3a' : '#e8f5f4', color: isDarkMode ? '#5eead4' : '#1d4746' }}>Lesson</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
              <div className="bg-stone-50 dark:bg-slate-900 p-4 rounded-lg border border-stone-100 dark:border-slate-700">
                <p className="font-medium text-stone-800 dark:text-slate-200 text-base mb-2">Course</p>
                <p className="text-sm text-stone-500 dark:text-slate-400">Bahasa yang kamu pelajari (contoh: Bahasa Jerman)</p>
              </div>
              <div className="bg-stone-50 dark:bg-slate-900 p-4 rounded-lg border border-stone-100 dark:border-slate-700">
                <p className="font-medium text-stone-800 dark:text-slate-200 text-base mb-2">Subcourse</p>
                <p className="text-sm text-stone-500 dark:text-slate-400">Level pembelajaran (A1, A2, dst)</p>
              </div>
              <div className="bg-stone-50 dark:bg-slate-900 p-4 rounded-lg border border-stone-100 dark:border-slate-700">
                <p className="font-medium text-stone-800 dark:text-slate-200 text-base mb-2">Lesson</p>
                <p className="text-sm text-stone-500 dark:text-slate-400">Materi spesifik di dalam level tersebut (grammar, struktur kalimat, topik percakapan)</p>
        </div>
      </div>
      
          </div>
                </div>

        {/* Row 3: Dua Jenis Practice */}
        <div className="rounded-lg overflow-hidden border border-stone-200 dark:border-slate-700 mb-8">
          <SectionTitle>Dua Jenis Practice</SectionTitle>
          <div className="bg-white dark:bg-slate-800 p-6">
            <p className="text-base text-stone-600 dark:text-slate-400 mb-5">Bedanya apa?</p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Lesson Practice */}
              <div className="bg-stone-50 dark:bg-slate-900 p-5 rounded-lg border border-stone-100 dark:border-slate-700">
                <h3 className="font-medium text-stone-800 dark:text-slate-200 text-lg mb-3">🎯 Lesson Practice</h3>
                <p className="text-base text-stone-600 dark:text-slate-400 mb-4">Latihan khusus dari satu lesson.</p>
                <p className="text-sm font-medium text-stone-700 dark:text-slate-300 mb-2">Gunakan ini untuk:</p>
                <ul className="text-sm text-stone-500 dark:text-slate-400 space-y-1">
                  <li>· Memahami konsep baru</li>
                  <li>· Memastikan kamu benar-benar paham materi</li>
                  <li>· Latihan dengan scope sempit & terarah</li>
                </ul>
              </div>

              {/* Subcourse Practice */}
              <div className="bg-stone-50 dark:bg-slate-900 p-5 rounded-lg border border-stone-100 dark:border-slate-700">
                <h3 className="font-medium text-stone-800 dark:text-slate-200 text-lg mb-3">🧠 Subcourse Practice</h3>
                <p className="text-base text-stone-600 dark:text-slate-400 mb-4">Latihan gabungan dari seluruh lesson dalam satu level (misalnya A1).</p>
                <p className="text-sm font-medium text-stone-700 dark:text-slate-300 mb-2">Gunakan ini untuk:</p>
                <ul className="text-sm text-stone-500 dark:text-slate-400 space-y-1">
                  <li>· Menguji pemahaman menyeluruh</li>
                  <li>· Melihat kesiapan naik level</li>
                  <li>· Melatih adaptasi antar materi</li>
                </ul>
              </div>
            </div>

            {/* Practice Type Cards - Visual */}
            <p className="text-sm font-medium text-stone-700 dark:text-slate-300 mb-4">Jenis Practice:</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <PracticeCard 
                icon={Lightning}
                title="Quick Practice"
                description="Latihan interaktif dengan berbagai tipe soal: Fill in the Blank, Multiple Choice, Sequencing, Pairing, dan Passage."
              />
              <PracticeCard 
                icon={PencilSimple}
                title="Writing Practice"
                description="Latihan menulis kalimat atau paragraf. Dinilai oleh AI berdasarkan grammar dan struktur."
              />
              <PracticeCard 
                icon={Microphone}
                title="Speaking Practice"
                description="Latihan berbicara dengan pengucapan. Dinilai oleh AI berdasarkan pronunciation dan fluency."
              />
            </div>
          </div>
        </div>

        {/* Row 4: Level & Progress + Alur Belajar */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Level & Progress */}
          <div className="rounded-lg overflow-hidden border border-stone-200 dark:border-slate-700">
            <SectionTitle>Tentang Level & Progress</SectionTitle>
            <div className="bg-white dark:bg-slate-800 p-6">
              <h3 className="text-base font-medium text-stone-700 dark:text-slate-300 mb-3">AI Level</h3>
              <p className="text-base text-stone-600 dark:text-slate-400 mb-3">Level kamu ditentukan oleh:</p>
              <ul className="text-base text-stone-500 dark:text-slate-400 space-y-2 mb-4">
                <li>· Akurasi jawaban</li>
                <li>· Konsistensi performa</li>
                <li>· Hasil latihan (terutama Writing & Speaking)</li>
              </ul>
              <p className="text-sm text-stone-500 dark:text-slate-400 bg-stone-50 dark:bg-slate-900 px-4 py-3 rounded-lg border border-stone-100 dark:border-slate-700">
                <strong>Level bisa naik atau turun</strong> tergantung performa. Ini disengaja. Di dunia nyata, kemampuan juga begitu.
              </p>
            </div>
          </div>

          {/* Alur Belajar */}
          <div className="rounded-lg overflow-hidden border border-stone-200 dark:border-slate-700">
            <SectionTitle>Cara Belajar yang Disarankan</SectionTitle>
            <div className="bg-white dark:bg-slate-800 p-6">
              <p className="text-base text-stone-600 dark:text-slate-400 mb-4">Alur ideal:</p>
              <ol className="text-base text-stone-600 dark:text-slate-400 space-y-2">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full text-white text-sm flex-shrink-0" style={{ backgroundColor: '#1d4746' }}>1</span>
                  <span>Pilih Subcourse Practice</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full text-white text-sm flex-shrink-0" style={{ backgroundColor: '#1d4746' }}>2</span>
                  <span>Kerjakan semua jenis practice sampai Overall level kalian mencapai 80</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full text-white text-sm flex-shrink-0" style={{ backgroundColor: '#1d4746' }}>3</span>
                  <span>Kalau jawaban salah-salah, pelajari review</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full text-white text-sm flex-shrink-0" style={{ backgroundColor: '#1d4746' }}>4</span>
                  <span>Kalau masih salah-salah terus, coba pelajari lesson terkait</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 flex items-center justify-center rounded-full text-white text-sm flex-shrink-0" style={{ backgroundColor: '#1d4746' }}>5</span>
                  <span>Kembali kerjakan Subcourse Practice</span>
                </li>
              </ol>
            </div>
          </div>
        </div>

        {/* Row 5: Catatan Penting */}
        <div className="rounded-lg overflow-hidden border border-stone-200 dark:border-slate-700">
          <SectionTitle>Catatan Penting</SectionTitle>
          <div className="bg-white dark:bg-slate-800 p-6">
            <ul className="text-base text-stone-600 dark:text-slate-400 space-y-3">
              <li>· Latihan sebanyak-banyaknya. Kalau salah tinggal cek review.</li>
              <li>· Bahasa itu belajarnya lebih penting dipake terus. Jangan sampe karena takut salah jadinya ga nyoba.</li>
              <li>· Writing & Speaking dinilai oleh AI. AI ga akan ngetawain jawaban kalian se-silly apapun itu.</li>
            </ul>
          </div>
        </div>

      </div>

      {/* Modal */}
      {fullscreenVideo && (
        <div className="fixed inset-0 bg-black/95 flex items-center justify-center z-50 p-5" onClick={closeFullscreen}>
          <div className="relative w-full max-w-4xl aspect-video" onClick={(e) => e.stopPropagation()}>
            <button className="absolute -top-10 right-0 text-white/60 hover:text-white" onClick={closeFullscreen}>
              <X size={24} />
            </button>
            <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${fullscreenVideo.embedId}?autoplay=1`} title={fullscreenVideo.title} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
          </div>
        </div>
      )}
    </div>
  );
};

export default OverallPanel;
